import PropTypes from 'prop-types';
import { useState } from 'react';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { FormProvider, RHFCheckbox, RHFTextField } from '../../../components/hook-form';
import { decide } from '../store/workflowActions';

/**
 * Approve or reject a request in one approver role (HQ Finance or Department
 * In-Charge). `action` is the entry from the request's `available_actions`.
 *
 * A reason is required to reject. Approving a RED-rated repair needs an
 * explicit confirmation (FR-RT-04): the checkbox shows when the asset is rated
 * RED, and again if the server asks for it.
 */
export default function DecisionDialog({ open, onClose, request, action }) {
  const dispatch = useDispatch();
  const rejecting = action?.code === 'REJECT';
  const ratedRed = request?.type === 'REPAIR' && request?.asset?.rating?.level === 'RED';
  const [needsConfirm, setNeedsConfirm] = useState(ratedRed && !rejecting);

  const schema = Yup.object().shape({
    comment: rejecting ? Yup.string().trim().required('Give a reason for the rejection') : Yup.string(),
    confirmRed: Yup.boolean(),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: { comment: '', confirmRed: false },
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async ({ comment, confirmRed }) => {
    const result = await dispatch(
      decide(request.id, {
        role: action.role,
        decision: rejecting ? 'REJECTED' : 'APPROVED',
        comment,
        confirm_red_rating: Boolean(confirmRed),
      })
    );
    if (result?.ok) {
      onClose(true);
    } else if (result?.data?.code === 'red_rating_confirmation_required') {
      setNeedsConfirm(true);
    }
  };

  return (
    <Dialog maxWidth="xs" open={open} fullWidth onClose={() => onClose(false)}>
      <DialogTitle>{action?.label}</DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {request?.reference} — {request?.asset?.description}
            </Typography>
            {needsConfirm && !rejecting && (
              <Alert severity="error">
                This asset is rated RED (not eligible for repair). Confirm that you still want to approve, or consider
                disposal.
              </Alert>
            )}
            <RHFTextField
              multiline
              rows={3}
              name="comment"
              label={rejecting ? 'Reason for rejection' : 'Comment (optional)'}
            />
            {needsConfirm && !rejecting && (
              <RHFCheckbox name="confirmRed" label="I confirm approving a RED-rated repair" />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" variant="outlined" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            size="small"
            variant="contained"
            color={rejecting ? 'error' : 'primary'}
            loading={isSubmitting}
          >
            {rejecting ? 'Reject' : 'Approve'}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

DecisionDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  request: PropTypes.object,
  action: PropTypes.object,
};
